package com.eladfinish.bible.contest.ui.components.toolbar

import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.PlainTooltip
import androidx.compose.material3.Text
import androidx.compose.material3.TooltipAnchorPosition
import androidx.compose.material3.TooltipBox
import androidx.compose.material3.TooltipDefaults
import androidx.compose.material3.rememberTooltipState
import androidx.compose.runtime.Composable
import androidx.compose.ui.unit.dp
import com.eladfinish.bible.contest.platform.isWeb

/**
 * Wraps the given [content] with a Material3 tooltip **only when** [tooltipText] is not blank.
 *
 * This helper is intended for toolbar icons (including navigation / overflow buttons) to mimic
 * the classic Android behavior where a **long-press** shows a small label (e.g. "More options"
 * or "Navigate up").
 *
 * Behavior:
 * - If [tooltipText] is null/blank (after trimming) → renders [content] as-is (no tooltip).
 * - Otherwise → renders [content] inside a [TooltipBox] showing a [PlainTooltip] with the text.
 * - On the web target, always renders [content] as-is, tooltip text or not: `TooltipBox`'s own
 *   pointer-input gesture detector (for hover/long-press) swallows the tap before it reaches the
 *   anchor's `clickable` on Compose Multiplatform's Kotlin/JS canvas backend — confirmed
 *   in-browser (a real tooltip popup appeared instead of the click firing). There's no hover/
 *   long-press affordance to preserve on the web anyway.
 *
 * Notes:
 * - The tooltip text is usually taken from `contentDescription` to keep Accessibility and tooltip
 *   in sync.
 * - Uses Material3's tooltip APIs, which are currently experimental.
 *
 * @param tooltipText Text to display inside the tooltip. If blank, no tooltip is shown.
 * @param content The anchor content (e.g., an IconButton) that the tooltip is attached to.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
internal fun ToolbarTooltipIfNeeded(
    tooltipText: String?,
    content: @Composable () -> Unit,
) {
    val text = tooltipText?.trim().orEmpty()
    if (text.isBlank() || isWeb) {
        content()
        return
    }

    val tooltipState = rememberTooltipState()

    TooltipBox(
        positionProvider = TooltipDefaults.rememberTooltipPositionProvider(
            positioning = TooltipAnchorPosition.Below,
            spacingBetweenTooltipAndAnchor = CustomSpacingBetweenTooltipAndAnchor,
        ),
        tooltip = {
            PlainTooltip {
                Text(
                    text = text,
                )
            }
        },
        state = tooltipState,
    ) {
        content()
    }
}

private val CustomSpacingBetweenTooltipAndAnchor = 12.dp
